import { BrowserRouter as Router, Routes, Route, Navigate, useParams } from 'react-router-dom';
import { Suspense, lazy } from 'react';
import { Toaster } from 'react-hot-toast';
import { RouteLoadingFallback } from './components/common/RouteLoadingFallback';
import MainLayout from './layouts/MainLayout';
import Home from './pages/public/Home';
const OwnerFinanceLayout = lazy(() => import('./layouts/owner/OwnerFinanceLayout'));
const OwnerSellerProgramLayout = lazy(() => import('./layouts/owner/OwnerSellerProgramLayout'));
const OwnerCreatorProgramLayout = lazy(() => import('./layouts/owner/OwnerCreatorProgramLayout'));
// Lazy-loaded Pages (route-level code splitting)
const Shop = lazy(() => import('./pages/public/Shop'));
const ShopProductDirectory = lazy(() => import('./pages/public/ShopProductDirectory'));

/** Old hub URL → canonical designs-by-slug route */
function RedirectLegacyShopProductHub() {
  const { slug } = useParams<{ slug: string }>();
  const trimmed = slug?.trim();
  if (!trimmed) return <Navigate to="/shop" replace />;
  return <Navigate to={`/shop/designs/${trimmed}`} replace />;
}
const Store = lazy(() => import('./pages/public/Store'));
const PublicDesignTool = lazy(() => import('./pages/public/DesignTool'));
const ProductDetails = lazy(() => import('./pages/public/ProductDetails'));
const About = lazy(() => import('./pages/public/About'));
const Terms = lazy(() => import('./pages/public/legal/Terms'));
const Privacy = lazy(() => import('./pages/public/legal/Privacy'));
const Refunds = lazy(() => import('./pages/public/legal/Refunds'));
const Shipping = lazy(() => import('./pages/public/Shipping'));
const SizeGuide = lazy(() => import('./pages/public/SizeGuide'));
const Contact = lazy(() => import('./pages/public/Contact'));
const TrackOrderLookup = lazy(() => import('./pages/public/TrackOrderLookup'));

const Login = lazy(() => import('./pages/auth/Login').then(m => ({ default: m.Login })));
const Register = lazy(() => import('./pages/auth/Register').then(m => ({ default: m.Register })));
const SellerOnboarding = lazy(() => import('./pages/auth/SellerOnboarding').then(m => ({ default: m.SellerOnboarding })));
const ForgotPassword = lazy(() => import('./pages/auth/ForgotPassword').then(m => ({ default: m.ForgotPassword })));
const ResetPassword = lazy(() => import('./pages/auth/ResetPassword').then(m => ({ default: m.ResetPassword })));
const SetPassword = lazy(() => import('./pages/auth/SetPassword').then(m => ({ default: m.SetPassword })));

const AccountDashboard = lazy(() => import('./pages/account/Dashboard'));
const MyDesigns = lazy(() => import('./pages/account/Designs'));
const MyOrders = lazy(() => import('./pages/account/Orders'));
const Wishlist = lazy(() => import('./pages/account/Wishlist'));
const AccountSettings = lazy(() => import('./pages/account/Settings'));

const CreatorApply = lazy(() => import('./pages/creator/Apply'));
const CreatorProgram = lazy(() => import('./pages/creator/CreatorProgram'));
const CreatorOnboarding = lazy(() => import('./pages/creator/Onboarding'));
const CreatorDashboard = lazy(() => import('./pages/creator/Dashboard'));
const CreatorLinks = lazy(() => import('./pages/creator/Links'));
const CreatorStorefront = lazy(() => import('./pages/creator/Storefront'));
const CreatorProducts = lazy(() => import('./pages/creator/Products'));
const CreatorCollections = lazy(() => import('./pages/creator/Collections'));
const CreatorCampaigns = lazy(() => import('./pages/creator/Campaigns'));
const CreatorAnalytics = lazy(() => import('./pages/creator/Analytics'));
const CreatorEarnings = lazy(() => import('./pages/creator/Earnings'));
const CreatorOrders = lazy(() => import('./pages/creator/Orders'));
const CreatorWalletGuide = lazy(() => import('./pages/creator/WalletGuide'));
const CreatorSettings = lazy(() => import('./pages/creator/Settings'));
const CreatorAchievements = lazy(() => import('./pages/creator/Achievements'));
const CreatorLeaderboard = lazy(() => import('./pages/creator/Leaderboard'));
const CreatorAssets = lazy(() => import('./pages/creator/Assets'));
const CreatorNotificationsRedirect = lazy(() => import('./pages/creator/Notifications'));
const PublicCreatorStorefront = lazy(() => import('./pages/creator/PublicStorefront'));
const PublicCreatorCollection = lazy(() => import('./pages/creator/PublicCollection'));
const CreatorMainLayout = lazy(() => import('./layouts/CreatorMainLayout'));
const CreatorProgramLayout = lazy(() => import('./layouts/CreatorProgramLayout'));
const CreatorApplicationsAdminPage = lazy(() => import('./pages/owner/creator-program/CreatorApplicationsAdminPage'));
const TaxonomyAdminPage = lazy(() => import('./pages/owner/taxonomy/TaxonomyAdminPage'));
const CreatorDirectoryAdminPage = lazy(() => import('./pages/owner/creator-program/CreatorDirectoryAdminPage'));
const CreatorFinanceAdminPage = lazy(() => import('./pages/owner/creator-program/CreatorFinanceAdminPage'));
const CreatorCampaignsAdminPage = lazy(() => import('./pages/owner/creator-program/CreatorCampaignsAdminPage'));
const CreatorProgramConfigAdminPage = lazy(() => import('./pages/owner/creator-program/CreatorProgramConfigAdminPage'));
const SellerApplicationsAdminPage = lazy(() => import('./pages/owner/seller-program/SellerApplicationsAdminPage'));
const SellerDirectoryAdminPage = lazy(() => import('./pages/owner/seller-program/SellerDirectoryAdminPage'));
const SellerApply = lazy(() => import('./pages/seller-program/Apply'));
const SellerProgram = lazy(() => import('./pages/seller-program/SellerProgram'));
const SellerProgramLayout = lazy(() => import('./layouts/SellerProgramLayout'));

const SellerDashboard = lazy(() => import('./pages/seller/Dashboard'));
const SellerDesignsPage = lazy(() => import('./pages/seller/Products'));
const SellerOrders = lazy(() => import('./pages/seller/Orders'));
const SellerOrderDetail = lazy(() => import('./pages/seller/OrderDetail'));
const SellerEarnings = lazy(() => import('./pages/seller/Earnings'));
const SellerWalletGuide = lazy(() => import('./pages/seller/WalletGuide'));
const SellerSettings = lazy(() => import('./pages/seller/Settings'));
const SellerNotificationsRedirect = lazy(() => import('./pages/seller/Notifications'));
const SellerAnalytics = lazy(() => import('./pages/seller/Analytics'));
const SellerProgress = lazy(() => import('./pages/seller/Progress'));
const SellerStore = lazy(() => import('./pages/seller/Store'));
const SellerStorePerformance = lazy(() => import('./pages/seller/StorePerformance'));
const SellerStoreConversion = lazy(() => import('./pages/seller/StoreConversion'));
const StandaloneDesignTool = lazy(() => import('./pages/seller/StandaloneDesignTool'));
const ProductAssignmentOnboarding = lazy(() => import('./pages/seller/ProductAssignmentOnboarding'));
const ListingWizard = lazy(() => import('./pages/seller/ListingWizard'));


// Owner analytics layout & pages
const OwnerAnalyticsLayout = lazy(() => import('./layouts/OwnerAnalyticsDashboard'));
const OverviewPage = lazy(() => import('./pages/owner/analytics/OverviewPage'));
const OwnerActionItemsPage = lazy(() =>
  import('./pages/owner/dashboard/OwnerDashboardDrilldownPages').then((m) => ({ default: m.OwnerActionItemsPage })),
);
const OwnerTopSellersPage = lazy(() =>
  import('./pages/owner/dashboard/OwnerDashboardDrilldownPages').then((m) => ({ default: m.OwnerTopSellersPage })),
);
const OwnerTopCreatorsPage = lazy(() =>
  import('./pages/owner/dashboard/OwnerDashboardDrilldownPages').then((m) => ({ default: m.OwnerTopCreatorsPage })),
);
const OwnerTopProductsPage = lazy(() =>
  import('./pages/owner/dashboard/OwnerDashboardDrilldownPages').then((m) => ({ default: m.OwnerTopProductsPage })),
);
const OwnerTopDesignsPage = lazy(() =>
  import('./pages/owner/dashboard/OwnerDashboardDrilldownPages').then((m) => ({ default: m.OwnerTopDesignsPage })),
);
const OwnerPayoutQueuePage = lazy(() =>
  import('./pages/owner/dashboard/OwnerDashboardDrilldownPages').then((m) => ({ default: m.OwnerPayoutQueuePage })),
);
const OwnerRiskItemsPage = lazy(() =>
  import('./pages/owner/dashboard/OwnerDashboardDrilldownPages').then((m) => ({ default: m.OwnerRiskItemsPage })),
);
const OwnerRefundReturnsPage = lazy(() =>
  import('./pages/owner/dashboard/OwnerDashboardDrilldownPages').then((m) => ({ default: m.OwnerRefundReturnsPage })),
);
const OwnerProductionQueuePage = lazy(() =>
  import('./pages/owner/dashboard/OwnerDashboardDrilldownPages').then((m) => ({ default: m.OwnerProductionQueuePage })),
);
const OwnerPricingMarginPage = lazy(() =>
  import('./pages/owner/dashboard/OwnerDashboardDrilldownPages').then((m) => ({ default: m.OwnerPricingMarginPage })),
);
const OwnerProductionQualityPage = lazy(() =>
  import('./pages/owner/dashboard/OwnerDashboardDrilldownPages').then((m) => ({ default: m.OwnerProductionQualityPage })),
);
const DashboardPageTemplate = lazy(() => import('./pages/owner/analytics/DashboardPageTemplate'));

// Sales Pages
const RevenueOrders = lazy(() => import('./pages/owner/analytics/sales/RevenueOrders'));
const RefundsDiscounts = lazy(() => import('./pages/owner/analytics/refunds/RefundsDiscounts'));
const ChannelsAttribution = lazy(() => import('./pages/owner/analytics/sales/ChannelsAttribution'));
const PricingAOV = lazy(() => import('./pages/owner/analytics/sales/PricingAOV'));

// Products Pages
const DesignPerformance = lazy(() => import('./pages/owner/analytics/products/DesignPerformance'));
const VariantsSKUs = lazy(() => import('./pages/owner/analytics/products/VariantsSKUs'));
const TemplatesLibrary = lazy(() => import('./pages/owner/analytics/products/TemplatesLibrary'));

// Management Pages
const ProductManagementPage = lazy(() => import('./pages/owner/products/ProductCatalogPage'));
const ProductCreationPage = lazy(() => import('./pages/owner/products/ProductCreationPage'));
const OrderManagementPage = lazy(() => import('./pages/owner/orders/OrderManagementPage'));
const OrderDetailsPage = lazy(() => import('./pages/owner/orders/orderDetails/OrderDetailsPage'));
const ReturnRequestsQueuePage = lazy(() => import('./pages/owner/orders/ReturnRequestsQueuePage'));

// Customers Pages
const CustomersOverview = lazy(() => import('./pages/owner/analytics/customers/CustomersOverview'));
const Segments = lazy(() => import('./pages/owner/analytics/customers/Segments'));
const GeographyGrowth = lazy(() => import('./pages/owner/analytics/customers/GeographyGrowth'));
const CohortsLTV = lazy(() => import('./pages/owner/analytics/customers/CohortsLTV'));
const LoyaltyVIP = lazy(() => import('./pages/owner/analytics/customers/LoyaltyVIP'));

// Products Pages
const InventoryAlerts = lazy(() => import('./pages/owner/products/InventoryAlerts'));

// Alerts Pages
const OperationalAlerts = lazy(() => import('./pages/owner/analytics/alerts/OperationalAlerts'));
const CustomerSupport = lazy(() => import('./pages/owner/analytics/alerts/CustomerSupport'));
const PaymentsFraud = lazy(() => import('./pages/owner/analytics/alerts/PaymentsFraud'));
const ContentCompliance = lazy(() => import('./pages/owner/analytics/alerts/ContentCompliance'));

// Finance & Strategy Pages
const FinanceOverviewPage = lazy(() => import('./pages/owner/finance/FinanceOverviewPage'));
const SellerPayoutsAdminPage = lazy(() => import('./pages/owner/finance/SellerPayoutsAdminPage'));
const SellerFinanceDirectoryPage = lazy(() => import('./pages/owner/finance/SellerFinanceDirectoryPage'));
const StrategyGoals = lazy(() => import('./pages/owner/analytics/strategy/StrategyGoals'));
const UserManagementPage = lazy(() => import('./pages/owner/system/UserManagementPage'));
const SystemSettingsPage = lazy(() => import('./pages/owner/system/SystemSettingsPage'));
const ShippingSettingsPage = lazy(() => import('./pages/owner/system/shipping/ShippingSettingsPage'));
const OwnerAuditLogsPage = lazy(() => import('./pages/owner/system/OwnerAuditLogsPage'));
const RolesPermissionsPage = lazy(() => import('./pages/owner/system/RolesPermissionsPage'));

const AuthLayout = lazy(() => import('./layouts/AuthLayout'));
const SellerMainLayout = lazy(() => import('./layouts/SellerMainLayout'));
const AccountLayout = lazy(() => import('./layouts/AccountLayout'));
// Other Pages
const NotFound = lazy(() => import('./pages/NotFound').then(m => ({ default: m.NotFound })));
const Cart = lazy(() => import('./pages/checkout/Cart'));
const Checkout = lazy(() => import('./pages/checkout/Checkout'));
const OrderConfirmation = lazy(() => import('./pages/checkout/OrderConfirmation'));
const TrackOrder = lazy(() => import('./pages/checkout/TrackOrder'));
const OrderDetail = lazy(() => import('./pages/account/OrderDetail'));
// Removed HomeOld route (contained placeholder/mock content)
import ScrollToTop from './components/helpers/ScrollToTop';
const EditorPage = lazy(() => import('./editor/EditorPage'));
const DesignPreviewPage = lazy(() => import('./pages/DesignPreviewPage'));
const ProductCustomizePage = lazy(() => import('./pages/ProductCustomizePage'));
const ImmersiveCustomizePage = lazy(() => import('./pages/ImmersiveCustomizePage'));
import RemoveCanvasWrapper from './utils/RemoveCanvasWrapper';
import { ProtectedRoute } from './components/auth/ProtectedRoute';
import { SetPasswordRoute } from './components/auth/SetPasswordRoute';
import { SellerOnboardingGate } from './components/seller/SellerOnboardingGate';
import { SellerGamificationProvider } from './components/seller/gamification/SellerGamificationProvider';
import { SellerCelebrationModal } from './components/seller/gamification/SellerCelebrationModal';
import { PublicRoute } from './components/auth/PublicRoute';
import { AuthStateMonitor } from './components/auth/AuthStateMonitor';
import { DeferredGoogleOneTapMount } from './components/auth/DeferredGoogleOneTapMount';
import { TwoFactorAuthModal } from './components/auth/TwoFactorAuthModal';
import { CartPersistence } from './components/cart/CartPersistence';
import { CartBadgeRefresh } from './components/cart/CartBadgeRefresh';
import { ACTIVE_CART_PATH } from './config/publicCartRoutes';
import { UserRole } from './types/auth.types';
const Unauthorized = lazy(() => import('./pages/Unauthorized').then(m => ({ default: m.Unauthorized })));
import { useAuthInit } from './hooks/useAuthInit';
import { ACCOUNT_WISHLIST_PATH } from './config/accountPaths';
import {
  CREATOR_APPLY_PATH,
  CREATOR_ONBOARDING_PATH,
  CREATOR_PATHS,
  CREATOR_STUDIO_BASE,
} from './config/creatorProgramPaths';
import {
  SELLER_APPLY_PATH,
  SELLER_ONBOARDING_PATH,
  SELLER_PROGRAM_PATH,
} from './config/sellerProgramPaths';
import { ReferralAttributionTracker } from './components/creator/ReferralAttributionTracker';
import { AuthPromptProvider } from './components/auth/prompt';

export function App() {
  useAuthInit();

  return (
    <Router future={{ v7_startTransition: false, v7_relativeSplatPath: true }}>
      <AuthPromptProvider>
      <ScrollToTop />
      <RemoveCanvasWrapper />
      <AuthStateMonitor /> {/* ✅ Handles auth-based redirection */}
      <DeferredGoogleOneTapMount />
      <TwoFactorAuthModal />
      <CartPersistence /> {/* ✅ Handles seamless cart persistence across login/logout */}
      <CartBadgeRefresh />
      <ReferralAttributionTracker />
      <div className="min-h-screen bg-gray-50">
        <Suspense fallback={<RouteLoadingFallback />}>
        <Routes>
          {/* Auth shell — minimal chrome, no footer */}
          <Route element={<AuthLayout />}>
            <Route
              path="/login"
              element={
                <PublicRoute>
                  <Login />
                </PublicRoute>
              }
            />
            <Route
              path="/register"
              element={
                <PublicRoute>
                  <Register />
                </PublicRoute>
              }
            />
            <Route
              path="/forgot-password"
              element={
                <PublicRoute>
                  <ForgotPassword />
                </PublicRoute>
              }
            />
            <Route
              path="/reset-password/:token"
              element={
                <PublicRoute>
                  <ResetPassword />
                </PublicRoute>
              }
            />
            <Route
              path="/activate/:token"
              element={
                <PublicRoute>
                  <SetPassword />
                </PublicRoute>
              }
            />
            <Route
              path="/account/set-password"
              element={
                <SetPasswordRoute>
                  <SetPassword />
                </SetPasswordRoute>
              }
            />
          </Route>

          <Route path="/" element={<MainLayout />}>
            {/* Public Pages */}
            <Route index element={<Home />} />
            {/* (Removed) /home-old */}
            <Route path="shop/designs/:productSlug" element={<Shop />} />
            <Route path="shop/designs" element={<Shop />} />
            <Route path="shop/product/:slug" element={<RedirectLegacyShopProductHub />} />
            {/* Category-filtered marketplace — must stay after static /shop/designs* routes */}
            <Route path="shop/:productSlug?" element={<ShopProductDirectory />} />
            <Route path="store/:storeSlug" element={<Store />} />
            <Route path="design-tool2" element={<PublicDesignTool />} /> 
            <Route path="design-tool" element={<EditorPage />} /> 
            <Route path="design/:designId/customize" element={<ImmersiveCustomizePage />} />
            <Route path="design/:designId/customize-old" element={<ProductCustomizePage />} />
            <Route path="design-preview" element={<Navigate to="/design-tool" replace />} />
            <Route path="customize/preview/:draftId" element={<DesignPreviewPage />} />
            <Route path="preview" element={<DesignPreviewPage />} />
            <Route path="cart" element={<Cart />} />
            <Route
              path="public-cart"
              element={<Navigate to={ACTIVE_CART_PATH} replace />}
            />
            <Route path="checkout" element={<Checkout />} />
            <Route path="order/confirmation/:id" element={<OrderConfirmation />} />
            <Route path="order/track/:orderNumber" element={<TrackOrder />} />
            <Route path="product/:slug" element={<ProductDetails />} />
            {/* designSegment: legacy UUID or SEO `{slug}-{uuid}` */}
            <Route path="product/:slug/:designSegment" element={<ProductDetails />} />
            <Route path="about" element={<About />} />
            <Route path="creator-program" element={<CreatorProgram />} />
            <Route path="seller-program" element={<SellerProgram />} />
            <Route path="contact" element={<Contact />} />
            <Route path="track-order" element={<TrackOrderLookup />} />
            <Route path="shipping" element={<Shipping />} />
            <Route path="size-guide" element={<SizeGuide />} />
            <Route path="terms" element={<Terms />} />
            <Route path="privacy" element={<Privacy />} />
            <Route path="refunds" element={<Refunds />} />
            <Route
              path="favorites"
              element={<Navigate to={ACCOUNT_WISHLIST_PATH} replace />}
            />

            {/* Unauthorized Page */}
            <Route path="unauthorized" element={<Unauthorized />} />

            {/* Account Pages - Protected */}
            <Route path="account" element={
              <ProtectedRoute>
                <AccountLayout />
              </ProtectedRoute>
            }>
              <Route index element={<Navigate to="dashboard" replace />} />
              <Route path="dashboard" element={<AccountDashboard />} />
              <Route path="designs" element={<MyDesigns />} />
              <Route path="orders" element={<MyOrders />} />
              <Route path="orders/:id" element={<OrderDetail />} />
              <Route path="wishlist" element={<Wishlist />} />
              <Route path="settings" element={<AccountSettings />} />
            </Route>

            {/* 404 Page */}
            <Route path="*" element={<NotFound />} />
          </Route>

          {/* Public creator storefront — no dashboard chrome */}
          <Route path="/r/:slug" element={<PublicCreatorStorefront />} />
          <Route path="/r/:slug/c/:collectionSlug" element={<PublicCreatorCollection />} />

          {/* Seller Program apply — focused shell */}
          <Route element={<SellerProgramLayout />}>
            <Route
              path={SELLER_APPLY_PATH}
              element={
                <ProtectedRoute>
                  <SellerApply />
                </ProtectedRoute>
              }
            />
          </Route>

          {/* Creator Program apply + onboarding — focused shell (no shop navbar/footer) */}
          <Route element={<CreatorProgramLayout />}>
            <Route
              path={CREATOR_APPLY_PATH}
              element={
                <ProtectedRoute>
                  <CreatorApply />
                </ProtectedRoute>
              }
            />
            <Route
              path={CREATOR_ONBOARDING_PATH}
              element={
                <ProtectedRoute requiredRoles={[UserRole.CREATOR]}>
                  <CreatorOnboarding />
                </ProtectedRoute>
              }
            />
          </Route>

          {/* Creator studio — separate from seller dashboard */}
          <Route
            path={CREATOR_STUDIO_BASE}
            element={
              <ProtectedRoute requiredRoles={[UserRole.CREATOR]}>
                <CreatorMainLayout />
              </ProtectedRoute>
            }
          >
            <Route index element={<Navigate to={CREATOR_PATHS.dashboard} replace />} />
            <Route path="dashboard" element={<CreatorDashboard />} />
            <Route path="links" element={<CreatorLinks />} />
            <Route path="storefront" element={<CreatorStorefront />} />
            <Route path="products" element={<CreatorProducts />} />
            <Route path="collections" element={<CreatorCollections />} />
            <Route path="campaigns" element={<CreatorCampaigns />} />
            <Route path="analytics" element={<CreatorAnalytics />} />
            <Route path="earnings" element={<CreatorEarnings />} />
            <Route path="orders" element={<CreatorOrders />} />
            <Route path="wallet-guide" element={<CreatorWalletGuide />} />
            <Route path="settings" element={<CreatorSettings />} />
            <Route path="achievements" element={<CreatorAchievements />} />
            <Route path="leaderboard" element={<CreatorLeaderboard />} />
            <Route path="assets" element={<CreatorAssets />} />
            <Route path="notifications" element={<CreatorNotificationsRedirect />} />
          </Route>

          {/* Seller onboarding — authenticated sellers only, no dashboard chrome */}
          <Route
            path="/seller/onboarding"
            element={
              <ProtectedRoute requiredRoles={[UserRole.SELLER]}>
                <SellerOnboarding />
              </ProtectedRoute>
            }
          />

          {/* Standalone Seller Design Tool - Outside dashboard layout */}
          <Route 
            path="/seller/design-tool" 
            element={
              <ProtectedRoute requiredRoles={[UserRole.SELLER]}>
                <SellerOnboardingGate>
                  <StandaloneDesignTool />
                </SellerOnboardingGate>
              </ProtectedRoute>
            } 
          />
          <Route
            path="/seller/design/:designId/listing-wizard"
            element={
              <ProtectedRoute requiredRoles={[UserRole.SELLER]}>
                <SellerGamificationProvider>
                  <SellerOnboardingGate>
                    <ListingWizard />
                  </SellerOnboardingGate>
                  <SellerCelebrationModal />
                </SellerGamificationProvider>
              </ProtectedRoute>
            }
          />

          {/* Seller Pages - Only sellers can access */}
          <Route path="/seller" element={
            <ProtectedRoute requiredRoles={[UserRole.SELLER]}>
              <SellerMainLayout />
            </ProtectedRoute>
          }>
            <Route index element={<SellerDashboard />} />
            <Route path="dashboard" element={<SellerDashboard />} />
            <Route path="designs" element={<SellerDesignsPage />} />
            <Route path="designs/create" element={<Navigate to="/seller/design-tool" replace />} />
            {/* Legacy products route maintained for backwards compatibility */}
            <Route path="products" element={<Navigate to="/seller/designs" replace />} />
            <Route path="products/add" element={<Navigate to="/seller/design-tool" replace />} />
            <Route
              path="design/:designId/assign-products"
              element={
                <ProtectedRoute requiredRoles={[UserRole.SELLER]}>
                  <ProductAssignmentOnboarding />
                </ProtectedRoute>
              }
            />
            <Route path="orders" element={<SellerOrders />} />
            <Route path="orders/:orderId" element={<SellerOrderDetail />} />
            <Route path="store" element={<SellerStore />} />
            <Route path="store/performance" element={<SellerStorePerformance />} />
            <Route path="store/conversion" element={<SellerStoreConversion />} />
            <Route path="analytics" element={<SellerAnalytics />} />
            <Route path="progress" element={<SellerProgress />} />
            <Route path="earnings" element={<SellerEarnings />} />
            <Route path="wallet-guide" element={<SellerWalletGuide />} />
            <Route path="notifications" element={<SellerNotificationsRedirect />} />
            <Route path="settings" element={<SellerSettings />} />
          </Route>

          {/* Owner Dashboard - Professional URL Structure */}
          <Route path="/owner" element={
            <ProtectedRoute staffPortal>
              <OwnerAnalyticsLayout />
            </ProtectedRoute>
          }>
            {/* Dashboard Overview */}
            <Route index element={<OverviewPage />} />
            <Route path="dashboard" element={<OverviewPage />} />
            <Route path="dashboard/action-items" element={<OwnerActionItemsPage />} />
            <Route path="dashboard/top-sellers" element={<OwnerTopSellersPage />} />
            <Route path="dashboard/top-creators" element={<OwnerTopCreatorsPage />} />
            <Route path="dashboard/top-products" element={<OwnerTopProductsPage />} />
            <Route path="dashboard/top-designs" element={<OwnerTopDesignsPage />} />
            <Route path="dashboard/payout-queue" element={<OwnerPayoutQueuePage />} />
            <Route path="dashboard/risk" element={<OwnerRiskItemsPage />} />
            <Route path="dashboard/refunds" element={<OwnerRefundReturnsPage />} />
            <Route path="dashboard/production-queue" element={<OwnerProductionQueuePage />} />
            <Route path="dashboard/production-quality" element={<OwnerProductionQualityPage />} />
            <Route path="dashboard/pricing-margin" element={<OwnerPricingMarginPage />} />
            
            {/* Sales & Revenue */}
            <Route path="sales" element={<Navigate to="/owner/sales/revenue" replace />} />
            <Route path="sales/revenue" element={<RevenueOrders />} />
            <Route path="sales/refunds" element={<RefundsDiscounts />} />
            <Route path="sales/channels" element={<ChannelsAttribution />} />
            <Route path="sales/pricing" element={<PricingAOV />} />
            
            {/* Products & Inventory */}
            <Route path="products" element={<ProductManagementPage />} />
            <Route path="products/add" element={<ProductCreationPage />} />
            <Route path="products/edit/:id" element={<ProductCreationPage />} />
            <Route path="products/variants" element={<VariantsSKUs />} />
            <Route path="products/categories" element={<TaxonomyAdminPage />} />
            <Route path="products/inventory" element={<InventoryAlerts />} />
            <Route path="products/performance" element={<DesignPerformance />} />
            <Route path="products/templates" element={<TemplatesLibrary />} />
            
            {/* Order Management */}
            <Route path="orders" element={<OrderManagementPage />} />
            <Route path="orders/pending" element={<DashboardPageTemplate title="Pending Orders" description="View and manage pending orders." />} />
            <Route path="orders/processing" element={<DashboardPageTemplate title="Processing Orders" description="View orders being processed." />} />
            <Route path="orders/shipped" element={<DashboardPageTemplate title="Shipped Orders" description="View shipped orders." />} />
            <Route
              path="orders/refunds"
              element={<Navigate to="/owner/return-requests" replace />}
            />
            <Route path="orders/:orderId" element={<OrderDetailsPage />} />
            <Route path="return-requests" element={<ReturnRequestsQueuePage />} />
            
            {/* Customer Management */}
            <Route path="customers" element={<CustomersOverview />} />
            <Route path="customers/support" element={<DashboardPageTemplate title="Support Tickets" description="Manage customer support tickets." />} />
            <Route path="customers/segments" element={<Segments />} />
            <Route path="customers/geography" element={<GeographyGrowth />} />
            <Route path="customers/cohorts" element={<CohortsLTV />} />
            <Route path="customers/loyalty" element={<LoyaltyVIP />} />
            
            {/* Alerts & Monitoring */}
            <Route path="alerts" element={<Navigate to="/owner/alerts/operational" replace />} />
            <Route path="alerts/operational" element={<OperationalAlerts />} />
            <Route path="alerts/support" element={<CustomerSupport />} />
            <Route path="alerts/payments" element={<PaymentsFraud />} />
            <Route path="alerts/compliance" element={<ContentCompliance />} />
            
            {/* Shipping & Fulfillment */}
            <Route path="shipping" element={<Navigate to="/owner/shipping/print-queue" replace />} />
            <Route path="shipping/print-queue" element={<DashboardPageTemplate title="Print Queue" description="Manage print orders and production." />} />
            <Route path="shipping/labels" element={<DashboardPageTemplate title="Shipping Labels" description="Generate and print shipping labels." />} />
            <Route path="shipping/tracking" element={<DashboardPageTemplate title="Shipment Tracking" description="Track outgoing shipments." />} />
            <Route path="shipping/carriers" element={<DashboardPageTemplate title="Shipping Carriers" description="Manage shipping carrier integrations." />} />
            
            {/* Financial Management — legacy /finance/* paths redirect to real surfaces */}
            <Route path="finance" element={<OwnerFinanceLayout />}>
              <Route index element={<FinanceOverviewPage />} />
              <Route path="payouts" element={<SellerPayoutsAdminPage />} />
              <Route path="sellers" element={<SellerFinanceDirectoryPage />} />
            </Route>
            <Route path="finance/revenue" element={<Navigate to="/owner/sales/revenue" replace />} />
            <Route path="creator-program" element={<OwnerCreatorProgramLayout />}>
              <Route path="applications" element={<CreatorApplicationsAdminPage />} />
              <Route path="creators" element={<CreatorDirectoryAdminPage />} />
              <Route path="finance" element={<CreatorFinanceAdminPage />} />
              <Route path="campaigns" element={<CreatorCampaignsAdminPage />} />
              <Route path="config" element={<CreatorProgramConfigAdminPage />} />
            </Route>
            <Route path="seller-program" element={<OwnerSellerProgramLayout />}>
              <Route path="applications" element={<SellerApplicationsAdminPage />} />
              <Route path="sellers" element={<SellerDirectoryAdminPage />} />
            </Route>
            <Route path="finance/refunds" element={<Navigate to="/owner/return-requests" replace />} />
            <Route path="finance/taxes" element={<Navigate to="/owner/finance/payouts" replace />} />
            
            {/* Content Management */}
            <Route path="content" element={<Navigate to="/owner/content/designs" replace />} />
            <Route path="content/designs" element={<DashboardPageTemplate title="Designs" description="Manage designs and templates." />} />
            <Route path="content/categories" element={<DashboardPageTemplate title="Categories" description="Manage product categories." />} />
            <Route path="content/marketing" element={<DashboardPageTemplate title="Marketing" description="Manage marketing materials." />} />
            <Route path="content/media" element={<DashboardPageTemplate title="Media Library" description="Manage images, videos, and other assets." />} />
            
            {/* Analytics & Reporting */}
            <Route path="analytics" element={<Navigate to="/owner/analytics/business-intelligence" replace />} />
            <Route path="analytics/business-intelligence" element={<DashboardPageTemplate title="Business Intelligence" description="Advanced analytics and insights." />} />
            <Route path="analytics/sales" element={<DashboardPageTemplate title="Sales Analytics" description="Analyze sales performance." />} />
            <Route path="analytics/customers" element={<DashboardPageTemplate title="Customer Analytics" description="Analyze customer behavior." />} />
            <Route path="analytics/reports" element={<DashboardPageTemplate title="Reports" description="Generate custom reports." />} />
            
            {/* System Administration */}
            <Route path="system" element={<Navigate to="/owner/system/users" replace />} />
            <Route path="system/users" element={<UserManagementPage />} />
            <Route path="system/permissions" element={<RolesPermissionsPage />} />
            <Route path="system/settings" element={<SystemSettingsPage />} />
            <Route path="system/settings/shipping" element={<ShippingSettingsPage />} />
            <Route path="system/audit-logs" element={<OwnerAuditLogsPage />} />
            
            {/* Strategy & Planning */}
            <Route path="strategy" element={<StrategyGoals />} />
          </Route>


          {/* Redirect old routes to new professional structure */}
          <Route path="/owner/analytics/*" element={<Navigate to="/owner" replace />} />
          <Route path="/owner/premium/*" element={<Navigate to="/owner" replace />} />

          {/* 404 Page */}
          <Route path="*" element={<NotFound />} />
        </Routes>
        </Suspense>
      </div>
       {/* your layout / routes / components */}
      <Toaster position="top-center" />
      </AuthPromptProvider>
    </Router>
  );
}

export default App;
